import { Link, useParams } from '@tanstack/react-router'
import { DashboardLayout } from '@/components/app/DashboardLayout'
import { ChildAvatar } from '@/components/app/ChildAvatar'
import { RiskLevelBadge } from '@/components/app/RiskLevelBadge'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { CATEGORY_CONFIG, RISK_LEVEL_CONFIG } from '@/constants/dashboard.constants'
import { Search } from 'lucide-react'
import { QuestionnaireCategory, Assessment, Child, Prediction } from '@/types/dashboard.types'
import { ChildAPI, AssessmentAPI, AuthAPI } from '@/services/api'
import { useState, useEffect } from 'react'
import { ArrowLeft, ListChecks, Loader2 } from 'lucide-react'

function getChildAgeInMonths(child: Child): number {
  const direct = child.ageInMonths
  if (typeof direct === 'number' && Number.isFinite(direct)) {
    return direct
  }
  const dob = new Date(child.dateOfBirth)
  if (Number.isNaN(dob.getTime())) {
    return 0
  }
  const today = new Date()
  let months = (today.getFullYear() - dob.getFullYear()) * 12 + (today.getMonth() - dob.getMonth())
  if (today.getDate() < dob.getDate()) {
    months -= 1
  }
  return Math.max(0, months)
}

/** Parent report wording for Domain Analysis only (does not change global CATEGORY_CONFIG). */
const DOMAIN_ANALYSIS_DISPLAY_LABELS: Record<QuestionnaireCategory, string> = {
  [QuestionnaireCategory.SPEECH_SOUND]: 'Speech Sound',
  [QuestionnaireCategory.LANGUAGE_COMPREHENSION]: 'Language Comprehension',
  [QuestionnaireCategory.EXPRESSIVE_LANGUAGE]: 'Expressive Language',
  [QuestionnaireCategory.BEHAVIORAL]: 'Behavioral Social',
}

export const AssessmentReport = () => {
  const params = useParams({ strict: false }) as { assessmentId?: string }
  const assessmentId = params?.assessmentId

  const [user, setUser] = useState<any>(null)
  const [assessment, setAssessment] = useState<Assessment | null>(null)
  const [child, setChild] = useState<Child | null>(null)
  const [prediction, setPrediction] = useState<Prediction | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const u = await AuthAPI.me()
        setUser(u.user)

        if (assessmentId) {
          const aData: any = await AssessmentAPI.getAssessment(assessmentId)
          setAssessment(aData)
          if (aData.childId) {
            const cData = await ChildAPI.getChild(aData.childId)
            setChild(cData)
          }
          if (aData.prediction) {
            setPrediction(aData.prediction)
          }
        }
      } catch (err) {
        console.error("Failed to fetch report data", err)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [assessmentId])

  if (isLoading) {
    return (
      <DashboardLayout user={user || { firstName: 'User', lastName: '', email: '', role: 'PARENT' }}>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    )
  }

  // Early return for missing data
  if (!assessment || !child || !prediction) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-muted-foreground">Report not found</h2>
          <p className="text-sm text-muted-foreground mt-2">The requested assessment report could not be found.</p>
        </div>
      </div>
    )
  }

  const riskConfig = RISK_LEVEL_CONFIG[prediction.riskLevel as keyof typeof RISK_LEVEL_CONFIG]
  const childAgeMonths = getChildAgeInMonths(child)

  const normalizeDomainKeyToCategory = (key: string): QuestionnaireCategory => {
    const normalized = key.toUpperCase().replace(/-/g, '_')
    if (normalized === 'SPEECH_SOUND' || normalized === 'LEXICAL_SPEECH_PROXY') {
      return QuestionnaireCategory.SPEECH_SOUND
    }
    if (normalized === 'LANGUAGE_COMPREHENSION' || normalized === 'LANGUAGE_GRAMMAR') {
      return QuestionnaireCategory.LANGUAGE_COMPREHENSION
    }
    if (normalized === 'EXPRESSIVE_LANGUAGE') {
      return QuestionnaireCategory.EXPRESSIVE_LANGUAGE
    }
    if (
      normalized === 'BEHAVIORAL_SOCIAL' ||
      normalized === 'BEHAVIORAL' ||
      normalized === 'FLUENCY_SUPPORT'
    ) {
      return QuestionnaireCategory.BEHAVIORAL
    }
    return QuestionnaireCategory.BEHAVIORAL
  }

  const toDomainScoreEntries = (scores: Record<string, number>) => {
    return Object.keys(scores).map((k) => {
      const score = Number(scores[k])
      const max = 4
      const percentage = (score / max) * 100
      return {
        domain: normalizeDomainKeyToCategory(k),
        score,
        maxScore: max,
        percentage,
        interpretation: percentage >= 75 ? 'Good' : percentage >= 50 ? 'Monitor' : 'Needs attention',
      }
    })
  }

  const getNumericEntries = (source: unknown): Record<string, number> => {
    if (!source || typeof source !== 'object') {
      return {}
    }

    const entries: Record<string, number> = {}
    Object.entries(source as Record<string, unknown>).forEach(([key, value]) => {
      if (typeof value === 'number' && Number.isFinite(value)) {
        entries[key] = value
      }
    })

    return entries
  }

  // Normalize domain scores: backend may return `domainScores` (array) or `categoryScores` (object)
  const derivedDomainScores: any[] = (() => {
    if ((prediction as any).domainScores && Array.isArray((prediction as any).domainScores)) {
      return (prediction as any).domainScores
    }
    if ((prediction as any).domainScores && typeof (prediction as any).domainScores === 'object') {
      return toDomainScoreEntries((prediction as any).domainScores as Record<string, number>)
    }
    if ((prediction as any).categoryScores && typeof (prediction as any).categoryScores === 'object') {
      const rawCategoryScores = (prediction as any).categoryScores

      const modelDomainScores = getNumericEntries(rawCategoryScores.modelDomainScores)
      if (Object.keys(modelDomainScores).length > 0) {
        return toDomainScoreEntries(modelDomainScores)
      }

      const formDomainScores = getNumericEntries(rawCategoryScores.formDomainScores)
      if (Object.keys(formDomainScores).length > 0) {
        return toDomainScoreEntries(formDomainScores)
      }

      const numericEntries = getNumericEntries(rawCategoryScores)

      return toDomainScoreEntries(numericEntries)
    }
    return []
  })()

  /** Calendar date only — avoids midnight UTC showing as wrong local day for DOB. */
  const formatDateOfBirth = (dateString: string) => {
    const raw = dateString.includes('T') ? dateString.split('T')[0]! : dateString.slice(0, 10)
    const parts = raw.split('-').map(Number)
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    }
    const [y, m, d] = parts
    const local = new Date(y!, m! - 1, d!)
    return local.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <DashboardLayout user={user}>
      <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in duration-500">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="animate-in slide-in-from-left min-w-0 duration-700">
            <h1 className="text-2xl font-bold sm:text-3xl">Assessment Report</h1>
            <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <p className="text-muted-foreground mt-1">
                Generated on {formatDateTime(prediction.createdAt)}
              </p>
              <Badge variant="outline">Model v{prediction.modelVersion || 'unknown'}</Badge>
            </div>
          </div>
          <div className="flex w-full animate-in slide-in-from-right flex-col gap-2 duration-700 sm:w-auto sm:flex-row sm:flex-wrap">
            <Link to="/dashboard/assessment" className="w-full sm:w-auto">
              <Button variant="default" className="w-full gap-2 sm:w-auto">
                <ArrowLeft className="h-4 w-4" aria-hidden />
                Back to assessments
              </Button>
            </Link>
          </div>
        </div>

        {/* Child Information */}
        <Card className="border-transparent animate-in slide-in-from-top duration-700">
          <CardHeader>
            <CardTitle>Child Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-4">
              <ChildAvatar gender={child.gender} size="xl" />
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="text-2xl font-bold">
                    {child.firstName} {child.lastName}
                  </h3>
                  <p className="text-muted-foreground">
                    Age: {childAgeMonths} months ({Math.floor(childAgeMonths / 12)} years {childAgeMonths % 12}{' '}
                    months)
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 sm:gap-4">
                  <div>
                    <span className="text-muted-foreground">Gender:</span>{' '}
                    <span className="font-medium capitalize">{child.gender.toLowerCase()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Date of Birth:</span>{' '}
                    <span className="font-medium">{formatDateOfBirth(child.dateOfBirth)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Assessment Date:</span>{' '}
                    <span className="font-medium">{formatDateTime(assessment.completedAt!)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Assessed By:</span>{' '}
                    <span className="font-medium">
                      {user.firstName} {user.lastName}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Overall Result */}
        <Card
          className={`animate-in slide-in-from-bottom duration-700 bg-card ${riskConfig.reportCardClassName}`}
        >
          <CardHeader>
            <CardTitle>Overall Assessment Result</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className={`text-2xl font-bold ${riskConfig.headlineClassName}`}>
                  {riskConfig.label}
                </h3>
                <p className="text-muted-foreground">{riskConfig.description}</p>
              </div>
              <RiskLevelBadge riskLevel={prediction.riskLevel} size="lg" />
            </div>
          </CardContent>
        </Card>

        {/* Domain Analysis */}
        <Card className="border-transparent animate-in slide-in-from-bottom duration-700">
          <CardHeader>
            <CardTitle>Domain Analysis</CardTitle>
            <CardDescription>
              Averages from your answers by developmental area. These values are the same inputs sent to
              the risk model together with age and gender.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {derivedDomainScores.map((domainScore) => {
              const categoryConfig =
                CATEGORY_CONFIG[domainScore.domain as keyof typeof CATEGORY_CONFIG] ?? {
                  label: String(domainScore.domain),
                  Icon: Search,
                  color: '#64748b',
                }
              const CategoryIcon = categoryConfig.Icon
              const percentage = domainScore.percentage
              const domainTitle =
                DOMAIN_ANALYSIS_DISPLAY_LABELS[domainScore.domain as QuestionnaireCategory] ??
                categoryConfig.label

              return (
                <div key={domainScore.domain} className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted/50">
                      <CategoryIcon
                        className="h-6 w-6"
                        style={{ color: categoryConfig.color }}
                        aria-hidden
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-lg">{domainTitle}</h4>
                        <span className="text-sm font-medium">
                          {domainScore.score}/{domainScore.maxScore} ({percentage.toFixed(0)}%)
                        </span>
                      </div>
                      <Progress value={percentage} className="h-2 mb-2" />
                      <p className="text-sm text-muted-foreground">
                        {domainScore.interpretation}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </CardContent>
        </Card>

        {/* Recommendations — rule-based from risk + domain scores computed from this assessment's answers */}
        <Card className="border-transparent animate-in slide-in-from-bottom duration-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-primary" aria-hidden />
              Recommendations
            </CardTitle>
            <CardDescription>
              Suggestions built from this screening: your child&apos;s age, risk level (Random Forest), and
              area scores from your answers. Opening lines are tailored to those values; additional bullets follow
              clinical-style rules. They are not free-form model text and not learned from the transcript training set.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 list-none p-0 m-0">
              {Array.isArray(prediction.recommendations) &&
                prediction.recommendations.map((rec: string | { text?: string; category?: string; priority?: string }, index: number) => {
                  const text = typeof rec === 'string' ? rec : rec.text ?? ''
                  if (!text) return null
                  return (
                    <li
                      key={index}
                      className="flex gap-3 rounded-lg border border-border/60 bg-muted/30 px-4 py-3 text-sm leading-relaxed"
                    >
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
                        {index + 1}
                      </span>
                      <span>{text}</span>
                    </li>
                  )
                })}
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
